import { type NavigateFunction, type useSearchParams } from 'react-router-dom';
import { ReactRouterBridgeNotInitializedError } from './ReactRouterBridgeNotInitializedError';

type SetSearchParamsFunction = ReturnType<typeof useSearchParams>[1];

/**
 * Internal navigate function. Set by initialize() method.
 * Should not be accessed directly - use the public navigate() method instead.
 */
let internalNavigate: NavigateFunction | null = null;
let internalSetSearchParams: SetSearchParamsFunction | null = null;

/**
 * Provider object for React Router's navigate function.
 * Similar to angularInjectorProvider pattern.
 * Allows Angular code to access React Router functionality.
 */
type NavigateOptions = {
    preserveParams?: boolean;
    replace?: boolean;
    state?: unknown;
};

type InitializeOptions = {
    navigate: NavigateFunction | null;
    setSearchParams: SetSearchParamsFunction | null;
};

// Queue for navigations requested before React Router is initialized.
// This happens when Angular code (e.g., validation_responder's forwardToTargetUrl
// during ghost mode refresh) calls loadRoute() before React has committed its
// first render and fired the useEffect that initializes the navigate function.
type PendingNavigation = {
    destination: string | number;
    options: NavigateOptions;
};
let pendingNavigation: PendingNavigation | null = null;

function executeNavigation(navigateFn: NavigateFunction, destination: string | number, options: NavigateOptions) {
    if (typeof destination === 'number') {
        navigateFn(destination);
        return;
    }

    const { preserveParams = false, replace = false, state } = options;

    if (preserveParams) {
        const currentSearch = window.location.search;
        navigateFn(destination + currentSearch, { replace, state });
    } else {
        navigateFn(destination, { replace, state });
    }
}

export const reactRouterProvider = {
    /**
     * Initialize the provider with React Router's navigate function.
     * Should be called by useInitializeReactRouterProvider hook in FrontRoyalRouter.
     *
     * @param navigate - The navigate function from useNavigate() hook
     */
    initialize: ({ navigate, setSearchParams }: InitializeOptions) => {
        internalNavigate = navigate;
        internalSetSearchParams = setSearchParams;

        // Flush any navigation that was queued before initialization.
        // Always clear pending — if navigate is null (e.g., unmount or test cleanup),
        // the pending navigation is discarded rather than leaking to the next initialize call.
        const pending = pendingNavigation;
        pendingNavigation = null;
        if (navigate && pending) {
            executeNavigation(navigate, pending.destination, pending.options);
        }
    },

    /**
     * Navigate using React Router.
     * - Pass a string destination for standard navigation.
     * - Pass a number (e.g. -1) to move through history, like `history.go`.
     *
     * If React Router hasn't been initialized yet, the navigation is queued
     * and will be executed once initialization completes.
     *
     * @param destination - Path/URL string or history delta number.
     * @param options - Navigation flags
     */
    navigate: (destination: string | number, options: NavigateOptions = {}) => {
        // FIXME: it may be that the only place setting preserveParams to true is
        // scope.gotoSignIn(true, correctBrandConfig.urlPrefix); We need to test that that
        // works, consider if its really something we need to support.
        if (!internalNavigate) {
            // Queue the navigation to be executed when the router is initialized.
            // Only the last pending navigation is kept (later calls override earlier ones).
            pendingNavigation = { destination, options };
            return;
        }

        executeNavigation(internalNavigate, destination, options);
    },

    setSearchParams: (...args: Parameters<SetSearchParamsFunction>) => {
        if (!internalSetSearchParams) {
            throw new ReactRouterBridgeNotInitializedError();
        }

        internalSetSearchParams(...args);
    },
};
